/**
 * Timezone utilities for New York (America/New_York) timezone
 * All dates should be converted to/from New York timezone for consistency
 * 
 * IMPORTANT: All date operations in this codebase use New York timezone.
 * JavaScript Date objects are stored in UTC internally, but all comparisons
 * and calculations are done as if the dates represent New York time.
 */

import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { format } from 'date-fns';

const NY_TIMEZONE = 'America/New_York';

/** Map IANA timezone to short display abbreviation */
const TZ_ABBREV: Record<string, string> = {
  'America/New_York': 'ET',
  'America/Chicago': 'CT',
  'America/Denver': 'MT',
  'America/Los_Angeles': 'PT',
  'America/Phoenix': 'AZ',
  'America/Anchorage': 'AKT',
  'Pacific/Honolulu': 'HT',
  'America/Indiana/Indianapolis': 'ET',
  'America/Detroit': 'ET',
  'America/Kentucky/Louisville': 'ET',
  'America/Boise': 'MT',
  'America/Puerto_Rico': 'AST',
};

/**
 * Get short timezone abbreviation from IANA timezone string
 * @param tz - IANA timezone string (e.g., "America/Chicago")
 * @returns Short abbreviation (e.g., "CT"), defaults to "ET"
 */
export function getTimezoneAbbrev(tz: string | null | undefined): string {
  if (!tz) return 'ET';
  return TZ_ABBREV[tz] || 'ET';
}

/**
 * Format a local time string (no timezone) with a timezone label
 * @param localTimeStr - ISO-like string without timezone (e.g., "2025-02-23T14:30:00")
 * @param timezone - IANA timezone string for the label
 * @param formatStr - date-fns format string
 * @returns Formatted string with tz label (e.g., "Feb 23, 14:30 PT"), or '-'
 */
export function formatLocalTime(
  localTimeStr: string | null | undefined,
  timezone: string | null | undefined,
  formatStr: string = 'MMM d, HH:mm'
): string {
  if (!localTimeStr) return '-';
  const date = new Date(localTimeStr);
  if (isNaN(date.getTime())) return '-';
  const abbrev = getTimezoneAbbrev(timezone);
  return `${format(date, formatStr)} ${abbrev}`;
}

/**
 * Convert a date to New York timezone representation
 * @param date - Date to convert (can be string, Date, or null)
 * @returns Date object where getFullYear(), getMonth(), getDate(), etc. return NY timezone values, or null
 * 
 * This function takes a UTC timestamp and converts it to a Date object where
 * the local time components represent what they are in NY timezone.
 * 
 * Note: toZonedTime returns a Date where the local time components match the timezone.
 * We create a new Date from those components to ensure consistency.
 */
export function toNewYorkTime(date: Date | string | null | undefined): Date | null {
  if (!date) return null;
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return null;

  // Convert UTC date to NY timezone - this gives us a Date where local components = NY time
  const nyDate = toZonedTime(dateObj, NY_TIMEZONE);
  
  // Create a new Date with NY time components
  // This ensures the Date object's local time components represent NY time
  return new Date(
    nyDate.getFullYear(),
    nyDate.getMonth(),
    nyDate.getDate(),
    nyDate.getHours(),
    nyDate.getMinutes(),
    nyDate.getSeconds(),
    nyDate.getMilliseconds()
  );
}

/**
 * Get current date/time in New York timezone
 * @returns Date object representing current time in New York
 */
export function getNewYorkNow(): Date {
  const now = new Date();
  return toNewYorkTime(now) || now;
}

/**
 * Normalize date to start of day in New York timezone
 * @param date - Date to normalize
 * @returns Date at start of day (00:00:00) in New York timezone, properly converted to UTC
 */
export function normalizeToStartOfDayNY(date: Date | string | null | undefined): Date | null {
  const nyDate = toNewYorkTime(date);
  if (!nyDate) return null;

  // Create the start of day time in NY timezone and convert to UTC
  const startOfDayLocal = new Date(nyDate.getFullYear(), nyDate.getMonth(), nyDate.getDate(), 0, 0, 0, 0);

  // Convert from NY timezone to UTC - treats the date as if it's in NY timezone
  return fromZonedTime(startOfDayLocal, NY_TIMEZONE);
}

/**
 * Normalize date to end of day in New York timezone
 * @param date - Date to normalize
 * @returns Date at end of day (23:59:59.999) in New York timezone, properly converted to UTC
 */
export function normalizeToEndOfDayNY(date: Date | string | null | undefined): Date | null {
  const nyDate = toNewYorkTime(date);
  if (!nyDate) return null;

  // Create the end of day time in NY timezone and convert to UTC
  // This ensures that when we call .toISOString(), we get the correct UTC time
  // that corresponds to 23:59:59.999 in NY timezone
  const endOfDayLocal = new Date(nyDate.getFullYear(), nyDate.getMonth(), nyDate.getDate(), 23, 59, 59, 999);

  // Convert from NY timezone to UTC - treats the date as if it's in NY timezone
  return fromZonedTime(endOfDayLocal, NY_TIMEZONE);
}

/**
 * Get day of week (0 = Monday, 1 = Tuesday, ..., 6 = Sunday) in New York timezone
 * @param date - Date to get day of week for
 * @returns Day of week number (0-6) where 0 = Monday, 6 = Sunday
 */
export function getDayOfWeekNY(date: Date | string | null | undefined): number | null {
  if (!date) return null;
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return null;

  // Use toZonedTime to convert to NY timezone and get day of week directly
  // toZonedTime returns a Date where local components represent NY time
  // Calling getDay() on this gives us the day of week in NY timezone
  // JavaScript getDay() returns: 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  // We convert to: 0 = Monday, 1 = Tuesday, ..., 6 = Sunday
  const nyDate = toZonedTime(dateObj, NY_TIMEZONE);
  const jsDay = nyDate.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  // Convert: 0 (Sunday) -> 6, 1 (Monday) -> 0, 2 (Tuesday) -> 1, ..., 6 (Saturday) -> 5
  return jsDay === 0 ? 6 : jsDay - 1;
}

/**
 * Get hour of day (0-23) in New York timezone
 * @param date - Date to get hour for
 * @returns Hour of day (0-23) in NY timezone, or null if input is invalid
 */
export function getHourNY(date: Date | string | null | undefined): number | null {
  const nyDate = toNewYorkTime(date);
  if (!nyDate) return null;

  return nyDate.getHours();
}

/**
 * Get the start of the current week (Monday) in New York timezone
 * @returns Date representing Monday 00:00:00 of current week in NY timezone
 */
export function getStartOfWeekNY(): Date {
  const now = getNewYorkNow();
  const dayOfWeek = now.getDay();
  // Convert Sunday (0) to 7 for easier calculation
  const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  
  // Create Monday date in NY timezone
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysFromMonday, 0, 0, 0, 0);
  
  return monday;
}

/**
 * Format date as ISO string in New York timezone
 * @param date - Date to format
 * @returns ISO string in NY timezone
 */
export function toISOStringNY(date: Date | string | null | undefined): string | null {
  const nyDate = toNewYorkTime(date);
  if (!nyDate) return null;

  return nyDate.toISOString();
}

/**
 * Create a date in New York timezone from calendar components
 * @param year - Year (e.g., 2024)
 * @param month - Month (1-12, 1-indexed)
 * @param day - Day of month (1-31)
 * @param hours - Hours (0-23) in NY timezone
 * @param minutes - Minutes (0-59) in NY timezone
 * @param seconds - Seconds (0-59) in NY timezone
 * @returns Date object representing that NY time (in UTC internally)
 */
export function createDateInNY(
  year: number,
  month: number, // 1-12 (1-indexed)
  day: number,
  hours: number = 0,
  minutes: number = 0,
  seconds: number = 0
): Date {
  // Create a Date object with NY time components (treated as local time)
  // JavaScript Date constructor expects month to be 0-indexed (0-11), so subtract 1
  const localDate = new Date(year, month - 1, day, hours, minutes, seconds);
  
  // Convert from NY timezone to UTC
  // fromZonedTime treats the date as if it's in the specified timezone and converts to UTC
  return fromZonedTime(localDate, NY_TIMEZONE);
}

/**
 * Format a date to locale date string in New York timezone
 * @param date - Date to format (can be string, Date, or null)
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string in NY timezone, or empty string if invalid
 */
export function toLocaleDateStringNY(
  date: Date | string | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string {
  // Bare "YYYY-MM-DD" strings come from backend RPCs that already bucket by ET
  // (e.g. dashboard_outreach_daily_breakdown). new Date("YYYY-MM-DD") would
  // parse as UTC midnight and then converting back to ET shifts the label
  // one day earlier — every bar/label on a date-bucketed chart would be
  // mislabeled. Build a local Date from the components and format directly.
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [y, m, d] = date.split('-').map(Number);
    const local = new Date(y, m - 1, d);
    return local.toLocaleDateString('en-US', options);
  }

  const nyDate = toNewYorkTime(date);
  if (!nyDate) return '';

  return nyDate.toLocaleDateString('en-US', {
    timeZone: NY_TIMEZONE,
    ...options,
  });
}

/**
 * Format a date to locale time string in New York timezone
 * @param date - Date to format (can be string, Date, or null)
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted time string in NY timezone, or empty string if invalid
 */
export function toLocaleTimeStringNY(
  date: Date | string | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string {
  const nyDate = toNewYorkTime(date);
  if (!nyDate) return '';
  
  return nyDate.toLocaleTimeString('en-US', {
    timeZone: NY_TIMEZONE,
    ...options,
  });
}

/**
 * Format a date to locale string in New York timezone
 * @param date - Date to format (can be string, Date, or null)
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date/time string in NY timezone, or empty string if invalid
 */
export function toLocaleStringNY(
  date: Date | string | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string {
  const nyDate = toNewYorkTime(date);
  if (!nyDate) return '';

  return nyDate.toLocaleString('en-US', {
    timeZone: NY_TIMEZONE,
    ...options,
  });
}

/**
 * Get date components (year, month, day) in New York timezone
 * This is critical for correct date storage and URL params
 * @param date - Date to extract components from
 * @returns Object with year, month (1-12), day components in NY timezone
 */
export function getDateComponentsNY(date: Date | string | null | undefined): { year: number; month: number; day: number } | null {
  if (!date) return null;

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return null;

  // Get date string formatted in NY timezone
  const nyDateString = dateObj.toLocaleString('en-US', {
    timeZone: NY_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  // Parse "MM/DD/YYYY" format
  const [month, day, year] = nyDateString.split('/').map(Number);
  return { year, month, day };
}

/**
 * Format date to YYYY-MM-DD string in New York timezone
 * @param date - Date to format
 * @returns YYYY-MM-DD string in NY timezone, or null if invalid
 */
export function formatDateToStringNY(date: Date | string | null | undefined): string | null {
  const components = getDateComponentsNY(date);
  if (!components) return null;

  const { year, month, day } = components;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * Convert a Date object representing a calendar date in NY timezone to a Date object
 * that will display the same calendar date in the browser's local timezone
 * This is useful when passing dates to Calendar components (react-day-picker) that
 * interpret dates in local timezone
 * @param date - Date object representing a date in NY timezone
 * @returns Date object that displays the same calendar date in local timezone, or null
 */
export function convertNYDateToLocalCalendarDate(date: Date | string | null | undefined): Date | null {
  if (!date) return null;
  
  const components = getDateComponentsNY(date);
  if (!components) return null;
  
  const { year, month, day } = components;
  // Create a date in local timezone with the same calendar components
  // This ensures the Calendar component displays the correct date
  return new Date(year, month - 1, day, 12, 0, 0); // Use noon to avoid DST issues
}

/**
 * Parse an API date string and return as NY-adjusted Date object
 * Use this when receiving dates from the API (which are in UTC)
 * @param dateString - ISO date string from API
 * @returns Date object or null if invalid
 */
export function parseAPIDateToNY(dateString: string | null | undefined): Date | null {
  if (!dateString) return null;

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;

  return toNewYorkTime(date);
}
